import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, ExternalLink, MapPin, Loader2, Send, Check, AlertTriangle, Info, PlusCircle,
  ArrowUpRight, Reply,
} from 'lucide-react';
import { cn, mapUrl } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  fetchMduRequest, saveMduFeedback, putUnitsBackInScope,
  escalateMduRequest, returnMduEscalation,
  listMduRequestNotes, addMduRequestNote,
} from '@/hooks/useMduRequests';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { isGfArea } from '@uniticket/shared';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { AddBoxForm, type AddressMgmtContext } from '@/components/tickets/AddressManagementMenu';
import { CreateAddressForm } from '@/components/tickets/CreateAddressForm';
import type {
  MduRequestDetail as MduRequestDetailDto, MduCase, MduBuildingBreakdown, MduRequestNoteDto,
} from '@uniticket/shared';

const CASE_BANNER: Record<MduCase, string> = {
  1: 'border-sky-200 bg-sky-50 text-sky-800',
  2: 'border-amber-200 bg-amber-50 text-amber-800',
  3: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800',
  4: 'border-emerald-200 bg-emerald-50 text-emerald-800',
};

export function MduRequestDetail() {
  const { t } = useTranslation('tickets');
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const navigate = useNavigate();
  const confirmDmpWrite = useDmpWriteConfirm();

  const [detail, setDetail] = useState<MduRequestDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [refine, setRefine] = useState(false);

  // Cas-1/3 action sheet.
  const [action, setAction] = useState<'create' | 'addBox' | null>(null);

  // Cas-2 selection (out-of-scope LUs the reviewer ticks, keyed by jvid) + submit state.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [scopeSubmitting, setScopeSubmitting] = useState(false);

  // Escalade. Le message est un état DU COMPOSANT et non du dialogue : le ConfirmDialog reçoit son
  // `message` en ReactNode et le re-rend à chaque frappe — un état recréé là-dedans ferait sauter le
  // focus à chaque caractère (le bug documenté en tête de confirm-dialog.tsx).
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [escRemark, setEscRemark] = useState('');
  const [escalating, setEscalating] = useState(false);

  // Fil de commentaires internes (demande user 2026-09-14). Chargé à part du détail : c'est une table
  // à NOUS, tandis que le détail balaie le DMP — les lier ferait attendre le fil derrière une lecture
  // lente, et un échec du DMP emporterait des notes qui n'en dépendent pas.
  const [notes, setNotes] = useState<MduRequestNoteDto[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // Feedback panel.
  const [feedbackValue, setFeedbackValue] = useState<boolean>(true);
  const [comment, setComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  // The feedback verdict/comment is seeded ONCE per request id — from an existing feedback row if present,
  // else a per-case default (Cas 1/2/3 = an action is taken → "added/true"; Cas 4 = nothing added → "false"
  // + a mandatory comment). Tracking the seeded id means re-fetches (e.g. toggling the bus-number refine)
  // do NOT clobber the reviewer's unsaved edits.
  const feedbackInitFor = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!Number.isInteger(requestId)) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetchMduRequest(requestId, refine);
      setDetail(res.data);
      setSelected(new Set());
      if (feedbackInitFor.current !== res.data.id) {
        feedbackInitFor.current = res.data.id;
        if (res.data.feedback) {
          setFeedbackValue(res.data.feedback.feedback ?? true);
          setComment(res.data.feedback.commentsUf ?? '');
        } else {
          setFeedbackValue(res.data.mduCase !== 4); // Cas 4 (nothing added) defaults to "not added" + comment
          setComment('');
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else toast.error((err as Error)?.message || t('mdu.detail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [requestId, refine, t]);

  useEffect(() => { load(); }, [load]);

  // Le fil suit l'ID SEUL, pas `refine` : basculer l'appariement sur le numéro de boîte recharge le
  // détail, pas les notes — elles ne dépendent que de la demande.
  useEffect(() => {
    if (!Number.isInteger(requestId)) return;
    let alive = true;
    listMduRequestNotes(requestId)
      .then((res) => { if (alive) setNotes(res.data); })
      // Un fil illisible ne doit pas faire tomber l'écran : la demande reste traitable sans lui.
      .catch(() => { if (alive) setNotes([]); });
    return () => { alive = false; };
  }, [requestId]);

  async function submitNote() {
    const body = noteDraft.trim();
    if (!body || noteSaving) return;
    setNoteSaving(true);
    try {
      const res = await addMduRequestNote(requestId, body);
      setNotes(res.data);
      setNoteDraft('');
    } catch (err) {
      toast.error((err as Error)?.message || t('mdu.detail.noteError'));
    } finally {
      setNoteSaving(false);
    }
  }

  if (loading) {
    return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div>;
  }
  if (notFound || !detail) {
    return (
      <div className="space-y-4">
        <BackLink label={t('mdu.detail.back')} />
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-400">
          {t('mdu.detail.notFound')}
        </div>
      </div>
    );
  }

  const d = detail;
  const addr = [d.streetName, d.hnrFull].filter(Boolean).join(' ');
  // La règle GF vient de shared : elle n'est écrite qu'une fois, et la file s'en sert pareil.
  const gfCodes = d.areaCodes.filter(isGfArea);
  const placeLine = [d.municipality, d.city && d.city !== d.municipality ? d.city : null].filter(Boolean).join(' · ');

  // Context handed to the reused Create / Add-box forms (Cas 3 / Cas 1).
  // NB: no streetName pre-fill — the Create form gates the street combobox on a chosen postal code (a request
  // has none), and picking a postal code clears the street anyway, so a street pre-fill would be dead. The
  // reviewer selects the strict-referential street after the postal code (see spec §4, Cas 3).
  const createContext: AddressMgmtContext = {
    municipality: d.municipality,
    city: d.city,
    houseNumber: d.parsedHouseNumber || null,
    houseNumberExtension: d.parsedExtension || null,
    // Le type n'est pas transmis : il est DÉRIVÉ du numéro de boîte côté serveur. On passe donc la
    // boîte demandée, et le type suit — avec une boîte l'adresse naît MDU, sans elle elle naît SDU et
    // basculera à la première boîte ajoutée. (L'ancien `dwellingUnitType: 'MDU'` n'était lu nulle part.)
    busNumber: d.boxNumber,
    inScope: true,
  };
  const cas1Building = d.buildings[0] ?? null;
  const addBoxContext: AddressMgmtContext = {
    streetName: d.streetName,
    municipality: d.municipality,
    city: d.city,
    belmapBuildingId: cas1Building?.belmapBuildingId ?? d.matches[0]?.belmapBuildingId ?? null,
    jvid: d.matches[0]?.jvid ?? null,
    inScope: true,
  };

  const toggle = (jvid: number) => setSelected((s) => {
    const next = new Set(s);
    next.has(jvid) ? next.delete(jvid) : next.add(jvid);
    return next;
  });

  async function backInScope(building: MduBuildingBreakdown) {
    const jvids = building.units.filter((u) => selected.has(u.jvid)).map((u) => u.jvid);
    if (jvids.length === 0 || building.belmapBuildingId == null) return;
    if (!confirmDmpWrite('remettre les logements en scope')) return;
    setScopeSubmitting(true);
    try {
      const res = await putUnitsBackInScope(requestId, { buildingId: building.belmapBuildingId, jvids });
      toast.success(t('mdu.detail.backInScopeDone', { count: res.data.updated, inScope: res.data.nbInScope }));
      await load();
    } catch (err) {
      toast.error((err as Error)?.message || t('mdu.detail.backInScopeError'));
    } finally {
      setScopeSubmitting(false);
    }
  }

  async function doEscalate() {
    setEscalating(true);
    try {
      await escalateMduRequest(requestId, { remark: escRemark.trim() || null });
      toast.success(t('mdu.detail.escalated', { label: detail?.escalationTarget?.label ?? '' }));
      setEscalateOpen(false);
      setEscRemark('');
      // Retour à la file : le dossier est passé chez quelqu'un d'autre, il n'y a plus rien à y faire.
      navigate('/mdu-requests');
    } catch (err) {
      toast.error((err as Error)?.message || t('mdu.detail.escalateError'));
    } finally {
      setEscalating(false);
    }
  }

  async function doReturn() {
    setEscalating(true);
    try {
      await returnMduEscalation(requestId, { remark: escRemark.trim() || null });
      toast.success(t('mdu.detail.returned', { label: detail?.returnTarget?.label ?? '' }));
      setReturnOpen(false);
      setEscRemark('');
      navigate('/mdu-requests');
    } catch (err) {
      toast.error((err as Error)?.message || t('mdu.detail.returnError'));
    } finally {
      setEscalating(false);
    }
  }

  async function submitFeedback() {
    if (feedbackValue === false && !comment.trim()) {
      toast.error(t('mdu.detail.commentRequired'));
      return;
    }
    if (!confirmDmpWrite('enregistrer le feedback dans le DMP')) return;
    setFeedbackSubmitting(true);
    try {
      // Only carry a comment for a "not added" (false) verdict — never persist a stale justification under a
      // "resolved/added" (true) verdict the reviewer flipped back to.
      await saveMduFeedback(requestId, { feedback: feedbackValue, commentsUf: feedbackValue ? null : (comment.trim() || null) });
      toast.success(t('mdu.detail.feedbackSaved'));
      navigate('/mdu-requests');
    } catch (err) {
      toast.error((err as Error)?.message || t('mdu.detail.feedbackError'));
    } finally {
      setFeedbackSubmitting(false);
    }
  }

  return (
    <div className="space-y-5">
      <BackLink label={t('mdu.detail.back')} />

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('mdu.detail.title', { id: d.id })}</h2>
          <p className="text-sm text-slate-500">
            {[addr, placeLine].filter(Boolean).join(' — ') || '—'}
            {d.contractor && <span className="text-slate-400"> · {d.contractor}</span>}
          </p>
        </div>
      </div>

      {/* Request info */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-3">{t('mdu.detail.requestInfo')}</h3>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <Field label={t('mdu.detail.municipality')} value={d.municipality} />
          <Field label={t('mdu.detail.city')} value={d.city} />
          <Field label={t('mdu.detail.area')} value={d.area} />
          <Field label={t('mdu.detail.street')} value={d.streetName} />
          <Field label={t('mdu.detail.houseNumber')} value={d.hnrFull} />
          <Field label={t('mdu.detail.box')} value={d.boxNumber} />
          <Field label={t('mdu.detail.luClaimed')} value={d.luNumber} />
          <Field label={t('mdu.detail.buildingGroup')} value={d.buildingGroup} />
          <Field label={t('mdu.detail.timestamp')} value={d.timestamp} />
        </dl>
        {d.commentContractant && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <span className="font-medium text-slate-500">{t('mdu.detail.contractorComment')}: </span>
            {d.commentContractant}
          </p>
        )}
      </div>

      {/* Evidence + verification aids */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-1">{t('mdu.detail.evidence')}</h3>
        <p className="text-xs text-slate-400 mb-3">{t('mdu.detail.evidenceHint')}</p>
        <div className="flex flex-wrap gap-2">
          {d.ssvDocLink ? (
            <a href={d.ssvDocLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-rose/30 bg-brand-rose/[0.06] px-3 py-1.5 text-sm font-semibold text-brand-rose hover:bg-brand-rose/[0.1]">
              <ExternalLink className="w-4 h-4" /> {t('mdu.detail.ssvLink')}
            </a>
          ) : (
            <span className="text-xs text-slate-400">{t('mdu.detail.noSsvLink')}</span>
          )}
          {addr && (
            <a href={mapUrl([d.streetName, d.hnrFull, d.city, d.municipality])} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              <MapPin className="w-4 h-4" /> {t('mdu.detail.mapLink')}
            </a>
          )}
        </div>
      </div>

      {/* Computed case banner */}
      <div className={cn('rounded-2xl border p-4', CASE_BANNER[d.mduCase])}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold">{t(`mdu.case${d.mduCase}.title`)}</p>
            <p className="text-sm opacity-90">{t(`mdu.case${d.mduCase}.desc`)}</p>
          </div>
          <div className="text-right text-xs font-semibold whitespace-nowrap">
            {t('mdu.detail.counts', { total: d.nbLogements, scope: d.nbLogementsScopeV15 })}
            {d.buildings.length > 1 && (
              <div className="mt-0.5 flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" />{t('mdu.detail.multiBuilding', { count: d.buildings.length })}</div>
            )}
          </div>
        </div>
        {/* L'appariement exact n'a rien rendu : on montre l'élargi, et on dit pourquoi. Sans ça le
            relecteur lit un cas calculé sur des lignes qui ne portent pas le suffixe demandé, sans
            aucun moyen de le savoir. */}
        {d.suffixMismatch && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" role="note">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span className="text-xs text-amber-800">{t('mdu.detail.suffixMismatch', { hnr: d.hnrFull ?? '' })}</span>
          </div>
        )}

        {/* La zone de déploiement, quand elle est GF : ces dossiers ne sont pas traités par l'équipe AV
            (user, 2026-09-03). On informe, on ne bloque pas — le relecteur peut avoir une raison. */}
        {gfCodes.length > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2" role="note">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
            <span className="text-xs text-rose-800">{t('mdu.detail.gfBanner', { codes: gfCodes.join(' · ') })}</span>
          </div>
        )}

        {/* bus_number refine toggle (affects the match/counts) */}
        <label className="mt-3 flex items-center gap-2 text-xs cursor-pointer select-none">
          <input type="checkbox" checked={refine} onChange={(e) => setRefine(e.target.checked)}
            className="rounded border-slate-300 text-brand-rose focus:ring-brand-rose/40" />
          {t('mdu.detail.refineBus')}
        </label>
      </div>

      {/* Action zone by case */}
      {d.mduCase === 3 && (
        <ActionCard title={t('mdu.case3.action')} hint={t('mdu.detail.cas3Hint')}>
          <Button onClick={() => setAction('create')}><PlusCircle className="w-4 h-4 mr-1.5" />{t('mdu.case3.action')}</Button>
        </ActionCard>
      )}
      {d.mduCase === 1 && (
        <ActionCard title={t('mdu.case1.action')} hint={t('mdu.detail.cas1Hint')}>
          <Button onClick={() => setAction('addBox')} disabled={!addBoxContext.belmapBuildingId && !addBoxContext.jvid}>
            <PlusCircle className="w-4 h-4 mr-1.5" />{t('mdu.case1.action')}
          </Button>
        </ActionCard>
      )}

      {/* Matched buildings breakdown (+ Cas-2 back-in-scope checklist) */}
      {d.buildings.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
          <h3 className="text-sm font-bold text-slate-700">{t('mdu.detail.buildings')}</h3>
          {/* Le cas #149062 : « Rue de Mons 26 » apparie trois bâtiments qui s'affichent tous
              pareil. Ils ne diffèrent que par le code postal, parce que le rapprochement accepte la
              commune à défaut de la ville. Le dire ici, avant la liste, plutôt que de laisser le
              relecteur croire à un triplon. */}
          {d.postalCodes.length > 1 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Info className="inline-block w-3.5 h-3.5 mr-1 -mt-0.5" />
              {t('mdu.detail.multiPostal', { count: d.postalCodes.length })}
            </p>
          )}
          {d.buildings.map((b) => {
            const outOfScope = b.units.filter((u) => !u.scopelistV15).length;
            const selectedHere = b.units.filter((u) => selected.has(u.jvid)).length;
            return (
              <div key={String(b.belmapBuildingId)} className="rounded-xl border border-slate-100">
                <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 text-xs text-slate-500">
                  {/* L'ADRESSE d'abord, l'id ensuite (demande d'Anouk 2026-08-31 : la fiche montrait un id
                      de bâtiment sans dire de quelle adresse il s'agissait — illisible dès qu'il y en a
                      plusieurs). `houseNumberExtension` porte le numéro COMPLET côté DMP, donc on ne le
                      concatène PAS au numéro : le 43 s'afficherait « 4343 ». */}
                  <span className="min-w-0">
                    <b className="text-slate-700">
                      {[b.streetName, b.houseNumberExtension || b.houseNumber].filter(Boolean).join(' ') || '—'}
                    </b>
                    {/* Le CP et la localité : sans eux, trois bâtiments homonymes sont indiscernables. */}
                    {(b.postalCode || b.city) && (
                      <span className="ml-1.5 font-medium text-slate-600">
                        {[b.postalCode, b.city].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    {b.matchesRequestCity && (
                      <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 whitespace-nowrap"
                            title={t('mdu.detail.cityMatchTitle', { city: d.city ?? '' })}>
                        {t('mdu.detail.cityMatch')}
                      </span>
                    )}
                    {b.areaCode && isGfArea(b.areaCode) && (
                      <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 whitespace-nowrap"
                            title={t('mdu.detail.areaDmp') + ' · ' + b.areaCode}>
                        {b.areaCode}
                      </span>
                    )}
                    <span className="ml-1.5 text-slate-400">belmap_building_id · {b.belmapBuildingId ?? '—'}</span>
                  </span>
                  <span className="whitespace-nowrap">{t('mdu.detail.counts', { total: b.nbLogements, scope: b.nbLogementsScopeV15 })}</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                      {d.mduCase === 2 && <th className="px-3 py-1.5 w-8" />}
                      <th className="px-3 py-1.5 font-semibold">{t('mdu.detail.colJvid')}</th>
                      <th className="px-3 py-1.5 font-semibold">{t('mdu.detail.colBus')}</th>
                      <th className="px-3 py-1.5 font-semibold">{t('mdu.detail.colDut')}</th>
                      <th className="px-3 py-1.5 font-semibold">{t('mdu.detail.colScope')}</th>
                      <th className="px-3 py-1.5 font-semibold">{t('mdu.detail.colStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.units.map((u) => {
                      const inScope = !!u.scopelistV15;
                      return (
                        <tr key={u.jvid} className="border-t border-slate-100">
                          {d.mduCase === 2 && (
                            <td className="px-3 py-1.5">
                              <input type="checkbox" disabled={inScope} checked={selected.has(u.jvid)}
                                onChange={() => toggle(u.jvid)}
                                title={inScope ? t('mdu.detail.alreadyInScope') : undefined}
                                className="rounded border-slate-300 text-brand-rose focus:ring-brand-rose/40 disabled:opacity-30" />
                            </td>
                          )}
                          <td className="px-3 py-1.5 font-medium text-slate-700">{u.jvid}</td>
                          <td className="px-3 py-1.5 text-slate-600">{u.busNumber?.trim() || '—'}</td>
                          <td className="px-3 py-1.5 text-slate-600">{u.dwellingUnitType || '—'}</td>
                          <td className="px-3 py-1.5">
                            {inScope
                              ? <span className="text-emerald-600">{u.scopelistV15}</span>
                              : <span className="text-amber-600 font-medium">{t('mdu.detail.notInScope')}</span>}
                          </td>
                          <td className="px-3 py-1.5 text-slate-500">{u.status || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {d.mduCase === 2 && (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      {outOfScope === 0 ? t('mdu.detail.allInScope') : t('mdu.detail.outOfScopeCount', { count: outOfScope })}
                    </span>
                    <Button size="sm" disabled={selectedHere === 0 || scopeSubmitting || b.belmapBuildingId == null}
                      onClick={() => backInScope(b)}>
                      {scopeSubmitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                      {t('mdu.detail.backInScope', { count: selectedHere })}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {d.mduCase === 2 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Info className="inline-block w-3.5 h-3.5 mr-1 -mt-0.5" />{t('mdu.detail.cas2Precheck')}
            </p>
          )}
        </div>
      )}

      {/* Commentaire interne (demande user 2026-09-14) — AU-DESSUS de l'escalade, parce que c'est ce
          qu'on écrit AVANT de passer la main : le mot qui explique pourquoi on escalade, ou qu'on se
          laisse pour la prochaine fois. Il ne part nulle part — le canal vers le sous-traitant reste
          le feedback, plus bas, qui écrit côté DMP. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-1">{t('mdu.detail.notesTitle')}</h3>
        <p className="text-xs text-slate-400 mb-3">{t('mdu.detail.notesHint')}</p>

        <Textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder={t('mdu.detail.notePlaceholder')}
          className="text-sm"
        />
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={submitNote}
            // Vide = rien à poser. Une note ne se supprime pas : mieux vaut un bouton inerte qu'une
            // ligne blanche définitive dans le fil.
            disabled={noteSaving || !noteDraft.trim()}
          >
            {noteSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {t('mdu.detail.noteSubmit')}
          </Button>
        </div>

        {notes.length > 0 && (
          <ul className="mt-4 space-y-2 border-t border-slate-100 pt-3">
            {notes.map((n) => (
              <li key={n.id} className="rounded-lg bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold text-slate-500">
                  {n.authorName} · {new Date(n.createdAt).toLocaleString()}
                </p>
                {/* whitespace-pre-wrap : un commentaire se tape avec des retours à la ligne, et les
                    perdre transformerait une liste de points en un pavé. */}
                <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-700">{n.body}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Escalade — DÉLIBÉRÉMENT au-dessus du panneau Feedback et hors de lui.
          L'écran se lit preuves → cas → action → verdict ; « je ne sais pas trancher » se place juste
          avant le verdict, comme son alternative. Dans la carte Feedback, ce bouton se lirait comme une
          troisième valeur de verdict — sur une écriture DMP irréversible. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-1">{t('mdu.detail.escalateTitle')}</h3>
        <p className="text-xs text-slate-400 mb-3">{t('mdu.detail.escalateHint')}</p>

        {d.escalation?.status === 'ESCALATED' && (
          <p className="mb-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
            {t('mdu.detail.escalatedBanner', {
              name: d.escalation.escalatedToName ?? '—',
              date: d.escalation.escalatedAt ? new Date(d.escalation.escalatedAt).toLocaleString() : '—',
            })}
            {d.escalation.remark && (
              <span className="mt-1 block whitespace-pre-wrap text-orange-900">« {d.escalation.remark} »</span>
            )}
          </p>
        )}
        {d.escalation?.status === 'RETURNED' && (
          <p className="mb-3 rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-3 py-2 text-xs text-fuchsia-800">
            {t('mdu.detail.returnedBanner', {
              name: d.escalation.returnedByName ?? '—',
              date: d.escalation.returnedAt ? new Date(d.escalation.returnedAt).toLocaleString() : '—',
            })}
            {d.escalation.remark && (
              <span className="mt-1 block whitespace-pre-wrap text-fuchsia-900">« {d.escalation.remark} »</span>
            )}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => { setEscRemark(''); setEscalateOpen(true); }}
            disabled={escalating || !d.escalationTarget}
            title={d.escalationTarget ? undefined : t('mdu.detail.escalateNoTarget')}
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
          >
            <ArrowUpRight className="w-4 h-4 mr-1.5" />
            {d.escalationTarget
              ? t('mdu.detail.escalate', { label: d.escalationTarget.label })
              : t('mdu.detail.escalateNoTargetBtn')}
          </Button>
          {d.returnTarget && (
            <Button
              type="button"
              variant="outline"
              onClick={() => { setEscRemark(''); setReturnOpen(true); }}
              disabled={escalating}
              className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
            >
              <Reply className="w-4 h-4 mr-1.5" />
              {t('mdu.detail.returnBtn', { label: d.returnTarget.label })}
            </Button>
          )}
          {d.escalationTarget && (
            <span className="text-xs text-slate-400">
              {t('mdu.detail.escalatesTo', { label: d.escalationTarget.label })}
            </span>
          )}
        </div>
      </div>

      {/* Feedback panel */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-1">{t('mdu.detail.feedbackTitle')}</h3>
        <p className="text-xs text-slate-400 mb-3">{t('mdu.detail.feedbackHint')}</p>
        <div className="flex gap-2">
          <button type="button" onClick={() => setFeedbackValue(true)}
            className={cn('flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
              feedbackValue ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50')}>
            {t('mdu.detail.feedbackTrue')}
          </button>
          <button type="button" onClick={() => setFeedbackValue(false)}
            className={cn('flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
              !feedbackValue ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50')}>
            {t('mdu.detail.feedbackFalse')}
          </button>
        </div>
        {!feedbackValue && (
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            placeholder={t('mdu.detail.commentPlaceholder')} className="mt-3" />
        )}
        {d.feedback && <p className="mt-2 text-xs text-slate-400">{t('mdu.detail.alreadyHandled', { date: d.feedback.feedbackDate ?? '' })}</p>}
        <Button onClick={submitFeedback} disabled={feedbackSubmitting} className="mt-4 w-full">
          {feedbackSubmitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
          {t('mdu.detail.closeRequest')}
        </Button>
      </div>

      {/* Le message se saisit DANS le dialogue : cet écran n'a pas de champ message permanent comme la
          fenêtre de validation, et il n'y a ici aucune note Uniticket où le texte pourrait vivre — donc
          c'est le seul canal, et la confirmation reste grisée tant qu'il est vide. */}
      <ConfirmDialog
        open={escalateOpen}
        tone="orange"
        icon={<ArrowUpRight className="w-5 h-5" />}
        title={t('mdu.detail.escalate', { label: d.escalationTarget?.label ?? '' })}
        message={(
          <>
            <p>{t('mdu.detail.escalateConfirm', { label: d.escalationTarget?.label ?? '' })}</p>
            <Textarea
              value={escRemark}
              onChange={(e) => setEscRemark(e.target.value)}
              rows={3}
              placeholder={t('mdu.detail.escalateRemarkPlaceholder')}
              className="mt-3"
            />
          </>
        )}
        confirmLabel={t('mdu.detail.escalateConfirmBtn')}
        cancelLabel={t('mdu.detail.cancel')}
        busy={escalating}
        confirmDisabled={!escRemark.trim()}
        onCancel={() => setEscalateOpen(false)}
        onConfirm={() => void doEscalate()}
      />
      <ConfirmDialog
        open={returnOpen}
        tone="rose"
        icon={<Reply className="w-5 h-5" />}
        title={t('mdu.detail.returnBtn', { label: d.returnTarget?.label ?? '' })}
        message={(
          <>
            <p>{t('mdu.detail.returnConfirm', { label: d.returnTarget?.label ?? '' })}</p>
            <Textarea
              value={escRemark}
              onChange={(e) => setEscRemark(e.target.value)}
              rows={3}
              placeholder={t('mdu.detail.returnRemarkPlaceholder')}
              className="mt-3"
            />
          </>
        )}
        confirmLabel={t('mdu.detail.returnConfirmBtn')}
        cancelLabel={t('mdu.detail.cancel')}
        busy={escalating}
        confirmDisabled={!escRemark.trim()}
        onCancel={() => setReturnOpen(false)}
        onConfirm={() => void doReturn()}
      />

      {/* Cas-1/3 action sheet (reuses the existing Create / Add-box forms) */}
      <Sheet open={action !== null} onOpenChange={(o) => { if (!o) setAction(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-6" onOpenAutoFocus={(e) => e.preventDefault()}>
          {action === 'create' && <CreateAddressForm context={createContext} onCreated={() => { setAction(null); load(); }} />}
          {action === 'addBox' && <AddBoxForm context={addBoxContext} onCreated={() => { setAction(null); load(); }} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function BackLink({ label }: { label: string }) {
  return (
    <Link to="/mdu-requests" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-rose">
      <ArrowLeft className="w-4 h-4" /> {label}
    </Link>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="text-slate-700">{value?.toString().trim() || '—'}</dd>
    </div>
  );
}

function ActionCard({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3">
      <div>
        <p className="text-sm font-bold text-slate-700">{title}</p>
        <p className="text-xs text-slate-400">{hint}</p>
      </div>
      {children}
    </div>
  );
}
